import * as React from 'react';

import TextInputWithMask from '../TextInputMask';
import { HelperText, useTheme } from 'react-native-paper';
import { View, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import useTimeInput from './inputUtils';
import type { TimePickerInputProps } from './TimePickerInput.shared';
import { useLatest } from '@/components/CustomRNPaper/Dates/utils';
import { TimePickerModal } from '@/components/CustomRNPaper/Dates/Time/TimePickerModal';
import TextInput from '@/components/CustomRNPaper/TextInput';

function TimePickerInput(
  {
    label,
    value,
    onChange,
    style,
    locale,
    withDateFormatInLabel = true,
    hasError,
    hideValidationErrors,
    onValidationError,
    modal,
    inputButton,
    saveLabel,
    saveLabelDisabled,
    uppercase,
    startYear,
    endYear,
    onChangeText,
    inputEnabled,
    disableStatusBarPadding,
    ...rest
  }: TimePickerInputProps & {
    modal?: (params: {
      value: TimePickerInputProps['value'];
      locale: TimePickerInputProps['locale'];
      saveLabel: TimePickerInputProps['saveLabel'];
      saveLabelDisabled: TimePickerInputProps['saveLabelDisabled'];
      uppercase: TimePickerInputProps['uppercase'];
      startYear: TimePickerInputProps['startYear'];
      endYear: TimePickerInputProps['endYear'];
      inputEnabled: TimePickerInputProps['inputEnabled'];
      disableStatusBarPadding: TimePickerInputProps['disableStatusBarPadding'];
    }) => any;
    inputButton?: React.ReactNode;
  },
  ref: any
) {
  const theme = useTheme();
  const {
    formattedValue,
    inputFormat,
    onChangeText: onDateInputChangeText,
    error,
  } = useTimeInput({
    locale,
    value,
    onChange,
    onValidationError,
  });

  let disabled;

  if (inputEnabled !== undefined) {
    disabled = !inputEnabled;
  }

  if (rest.disabled) {
    disabled = rest.disabled;
  }

  const [visible, setVisible] = React.useState<boolean>(false);
  const onDismiss = React.useCallback(() => {
    setVisible(false);
  }, [setVisible]);
  const onChangeRef = useLatest(onChange);
  const onInnerConfirm = React.useCallback(
    ({ hours, minutes }: any) => {
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      setVisible(false);
      onChangeRef.current(date);
    },
    [setVisible, onChangeRef]
  );

  return (
    <>
      <View style={styles.root}>
        <View style={styles.inputContainer}>
          <TextInputWithMask
            {...rest}
            ref={ref}
            label={
              typeof label === 'string' || typeof label === 'undefined' 
                ? getLabel({
                    label: label as any,
                    inputFormat,
                    withDateFormatInLabel,
                  })
                : label
            }
            value={formattedValue}
            keyboardType={rest.keyboardType ?? 'number-pad'}
            // @ts-ignore
            type={'time'}
            mask={inputFormat}
            disabled={disabled}
            onChangeText={onDateInputChangeText}
            onChange={(e) => onChangeText && onChangeText(e.nativeEvent.text)}
            keyboardAppearance={rest.keyboardAppearance ?? (theme.dark ? 'dark' : 'default')}
            error={(!!error && !hideValidationErrors) || !!hasError}
            style={[styles.input, style]}
            inputButton={
              <TextInput.Icon
                size={rest.iconSize ?? 28}
                icon={'clock-outline'}
                color={rest.iconColor ?? undefined}
                disabled={rest.disabled}
                onPress={() => setVisible(true)}
                style={rest.iconStyle as StyleProp<ViewStyle>}
                testID={`${rest.testID || 'date-picker'}-icon-button`}
              />
            }
          />
        </View>
        {error && !hideValidationErrors ? (
          <HelperText type="error" visible={!!error}>
            {error}
          </HelperText>
        ) : null}
      </View>
      <TimePickerModal
        hours={parseInt(formattedValue.split(':')[0])}
        minutes={parseInt(formattedValue.split(':')[1])}
        visible={visible}
        onDismiss={onDismiss}
        onConfirm={onInnerConfirm}
      />
    </>
  );
}

function getLabel({
  withDateFormatInLabel,
  inputFormat,
  label,
}: {
  withDateFormatInLabel: boolean;
  inputFormat: string;
  label: string | undefined;
}) {
  if (withDateFormatInLabel) {
    return label ? `${label} (${inputFormat})` : inputFormat;
  }
  return label || '';
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'flex-start',
    width: '100%',
  },
  inputContainer: {
    flexGrow: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    width: '100%',
  },
  input: {
    flexGrow: 1,
    width: '100%',
  },
});
export default React.forwardRef(TimePickerInput);
